/**
 * Handles automatic authentication for our iframe demos using
 * a sentinel marketing@pebble.finance account.
 *
 * NOTE: This uses in-memory storage instead of localStorage because
 * iframes often have localStorage blocked by browser security policies
 * (especially Safari and third-party cookie restrictions).
 */

const MARKETING_EMAIL = "marketing@pebble.finance";

interface TokenResponse {
  token: string;
  expires_in: number;
}

interface TokenCache {
  token: string;
  expiryTimestamp: number;
}

// In-memory token cache (doesn't rely on localStorage which may be blocked in iframes)
let tokenCache: TokenCache | null = null;

export function getMarketingEmail(): string {
  return MARKETING_EMAIL;
}

function isTokenExpired(cache: TokenCache | null): boolean {
  if (!cache) return true;

  // Consider expired if less than 5 minutes remaining
  const bufferSeconds = 5 * 60;
  return Date.now() / 1000 > cache.expiryTimestamp - bufferSeconds;
}

export function clearMarketingToken(): void {
  tokenCache = null;
}

async function createMarketingToken(): Promise<string> {
  const response = await fetch("/guest/create-token/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: MARKETING_EMAIL,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create marketing token: ${response.statusText}`);
  }

  const data: TokenResponse = await response.json();

  // Store in memory cache
  const expiryTimestamp = Math.floor(Date.now() / 1000) + data.expires_in;
  tokenCache = {
    token: data.token,
    expiryTimestamp,
  };

  return data.token;
}

export async function ensureMarketingToken(): Promise<string> {
  // Return cached token if valid
  if (tokenCache && !isTokenExpired(tokenCache)) {
    return tokenCache.token;
  }

  // Create new token if expired or missing
  return createMarketingToken();
}
